import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeoutMinutes, setTimeoutMinutes] = useState(3);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/auth/me`, { withCredentials: true });
      setAdmin(res.data);
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Fetch inactivity timeout setting once authenticated
  useEffect(() => {
    if (!admin) return;
    axios.get(`${API}/admin/inactivity-timeout`, { withCredentials: true })
      .then(res => setTimeoutMinutes(res.data.timeout_minutes || 3))
      .catch(() => {});
  }, [admin]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/admin/auth/logout`, {}, { withCredentials: true }); } catch {}
    setAdmin(null);
  }, []);

  // Inactivity auto-logout
  useEffect(() => {
    if (!admin) return;

    const resetActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));

    // Regular interval check (for active tab scenarios)
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - lastActivityRef.current) / 1000 / 60;
      if (elapsed >= timeoutMinutes) {
        logout();
      }
    }, 10000);

    // Critical: check IMMEDIATELY when tab becomes visible again.
    // This fires BEFORE mousedown/touchstart, so the stale lastActivity
    // timestamp is still intact and we can detect the real idle time.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = (Date.now() - lastActivityRef.current) / 1000 / 60;
        if (elapsed >= timeoutMinutes) {
          logout();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [admin, timeoutMinutes, logout]);

  const login = async (email, password, totpCode) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    const res = await axios.post(`${API}/admin/auth/login`, payload, { withCredentials: true });
    if (res.data.requires_2fa || res.data.requires_2fa_setup) {
      return res.data;
    }
    lastActivityRef.current = Date.now();
    setAdmin(res.data);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/admin/auth/register`, { email, password, name }, { withCredentials: true });
    return res.data;
  };

  const verify2FA = async (adminId, totpCode, setupToken) => {
    const res = await axios.post(`${API}/admin/auth/verify-2fa`, {
      admin_id: adminId,
      totp_code: totpCode,
      setup_token: setupToken || null,
    }, { withCredentials: true });
    lastActivityRef.current = Date.now();
    setAdmin(res.data);
    return res.data;
  };

  const hasPermission = (perm) => {
    if (!admin) return false;
    const rolePerms = {
      super_admin: ['can_manage_admins', 'can_manage_users', 'can_provision', 'can_view_dashboard', 'can_edit_settings'],
      admin: ['can_manage_users', 'can_provision', 'can_view_dashboard', 'can_edit_settings'],
      viewer: ['can_view_dashboard'],
    };
    return (rolePerms[admin.role] || []).includes(perm);
  };

  const refreshTimeout = useCallback(() => {
    axios.get(`${API}/admin/inactivity-timeout`, { withCredentials: true })
      .then(res => setTimeoutMinutes(res.data.timeout_minutes || 3))
      .catch(() => {});
  }, []);

  return (
    <AdminContext.Provider value={{ admin, loading, login, register, verify2FA, logout, hasPermission, checkAuth, timeoutMinutes, refreshTimeout }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}
